'use client';

import { useEffect, useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  ChevronRight,
  ExternalLink,
  Loader2,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { useDebounce } from '@/shared/hooks';
import { cn } from '@/shared/lib/cn';
import {
  useCatalogBrands,
  useCatalogCategories,
  useCatalogLines,
  useCatalogProducts,
  useTokenBearingAxes,
} from '../../hooks/use-catalog';
import { useBrandSelections } from '../../hooks/use-brand-selections';
import type { BrandsNavigation } from '../../hooks/use-brands-navigation';
import { BrandSelectionSummary } from './BrandSelectionSummary';
import { buildBrandToken } from '../../lib/brand-tokens';
import type { BrandSelection } from '@/types/brand';
import type {
  CatalogMarket,
  CatalogNotices,
  Product,
  ProductCategory,
} from '@/types/catalog';

/** Same ceiling the API enforces on `data.attributes.brand_selections`. */
const MAX_SELECTIONS = 10;

interface BrandsContentProps {
  /** The whole `settings.brand_selections` list, in pick order. */
  selections: string[] | null;
  onSelectionsChange: (selections: string[] | null) => void;
  /**
   * Where in the tree the picker stands. Owned by the caller so the SHEET's own
   * Back walks up a level instead of closing — see {@link useBrandsNavigation}.
   */
  nav: BrandsNavigation;
  variant?: 'default' | 'compact';
}

/**
 * Brands — pick a real manufacturer's product, at the precision you actually
 * have.
 *
 * ONE navigator for every library. Category → manufacturer → line → product,
 * with a chip row for each axis the category declares. Nothing here knows what
 * siding or paint is: the shape of a library comes from
 * `/v1/catalog/categories` (does it have a line level, what is it faceted by,
 * does a pick from it change the render), so adding roofing is a migration and
 * a seed rather than a third branch through this file.
 *
 * Two rules shape the whole thing and neither is decoration:
 *
 *  - **A pick does not have to reach the bottom.** Somebody who knows the house
 *    gets Hardie siding but has not chosen a colour should be able to say that
 *    much and no more. Selecting a level is therefore an explicit row ("Any
 *    product from James Hardie", "Any product in this line") pinned to the top
 *    of the screen you are on, and tapping a normal row always means "go
 *    deeper" — one gesture, one meaning.
 *  - **Every level shows what is picked beneath it**, so a selection made three
 *    screens down is visible from the settings row without opening anything.
 *    The labels come resolved from the API (`/v1/brand-selections`) rather than
 *    being assembled here, because the same summaries have to read identically
 *    on WEB, Apple and Android.
 *
 * The picks are a set, not a ranking. The user cannot reorder them, so nothing
 * here promises that the first one lands on the walls — the render prompt asks
 * the model to read the house and place each product itself.
 *
 * Honesty carried over from the per-library pickers: a product whose maker
 * publishes no colour value is drawn with NO swatch, a blend is never drawn
 * flat, and the caveats beside a row arrive already composed and localized from
 * the server instead of being re-derived here.
 */
export function BrandsContent({
  selections,
  onSelectionsChange,
  nav,
  variant = 'default',
}: BrandsContentProps) {
  const t = useTranslations('render.settings.brands');
  const isCompact = variant === 'compact';

  const {
    categorySlug,
    brandId,
    lineId,
    axisChoice,
    setAxisChoice,
    preferredSlugs,
    searchInput,
    setSearchInput,
    openCategory,
    openBrand,
    openLine,
    reveal,
    goToRoot,
  } = nav;

  const search = useDebounce(searchInput, 300);
  const isSearching = search.trim().length > 0;

  const picks = useMemo(() => selections ?? [], [selections]);
  const isFull = picks.length >= MAX_SELECTIONS;
  const { entries } = useBrandSelections(picks);

  const { data: catalog } = useCatalogCategories();
  const categories = catalog?.categories ?? [];
  const notices = catalog?.notices ?? null;
  const category = categories.find(
    (entry) => entry.attributes.slug === categorySlug
  );

  // Named here as well as inside the branch below, for the trail. Both are the
  // same cached queries — the catalogue is static reference data, so asking a
  // second time costs a cache read, not a request.
  const { data: brandsResult } = useCatalogBrands(categorySlug);
  const brandName = brandsResult?.brands.find((entry) => entry.id === brandId)
    ?.attributes.name;
  const { data: trailLines = [] } = useCatalogLines(
    category?.attributes.has_line_level ? categorySlug : null,
    brandId
  );
  const lineName = trailLines.find((entry) => entry.id === lineId)?.attributes
    .name;

  const toggle = (token: string) => {
    if (picks.includes(token)) {
      const next = picks.filter((entry) => entry !== token);
      onSelectionsChange(next.length > 0 ? next : null);
      return;
    }

    if (isFull) return;

    onSelectionsChange([...picks, token]);
  };

  const isPicked = (token: string) => picks.includes(token);

  return (
    <div className={cn('space-y-3', !isCompact && 'space-y-4')}>
      {category !== undefined && (
        <>
          <Breadcrumb
            steps={[
              { label: t('title'), open: goToRoot },
              {
                label: category.attributes.name,
                open: () => openCategory(category.attributes.slug),
              },
              ...(brandName !== undefined && brandId !== null
                ? [{ label: brandName, open: () => openBrand(brandId) }]
                : []),
              ...(lineName !== undefined && lineId !== null
                ? [{ label: lineName, open: () => openLine(lineId) }]
                : []),
            ]}
          />
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('searchPlaceholder')}
            clearLabel={t('searchClear')}
          />
        </>
      )}

      {entries.length > 0 && (
        <SettingsCard title={t('selected')}>
          {entries.map((entry) => (
            <SelectedPickRow
              key={entry.id}
              entry={entry}
              removeLabel={t('remove', { product: entry.attributes.title })}
              onReveal={() => reveal(entry)}
              onRemove={() => toggle(entry.id)}
            />
          ))}
        </SettingsCard>
      )}

      {category === undefined ? (
        <CategoryList
          categories={categories}
          entries={entries}
          onOpen={openCategory}
          emptyLabel={t('auto')}
        />
      ) : (
        <CategoryBranch
          category={category}
          brandId={brandId}
          lineId={lineId}
          axisChoice={axisChoice}
          preferredSlugs={preferredSlugs}
          search={search}
          isSearching={isSearching}
          isFull={isFull}
          isCompact={isCompact}
          entries={entries}
          isPicked={isPicked}
          onOpenBrand={openBrand}
          onOpenLine={openLine}
          onAxisChange={setAxisChoice}
          onClearSearch={() => setSearchInput('')}
          onToggle={toggle}
          t={t}
        />
      )}

      {notices && <Notices notices={notices} />}
    </div>
  );
}

type Translate = (key: string, values?: Record<string, string | number>) => string;

/* -------------------------------------------------------------------------- */
/*  Level 1 — the libraries the API says exist                                */
/* -------------------------------------------------------------------------- */

function CategoryList({
  categories,
  entries,
  onOpen,
  emptyLabel,
}: {
  categories: ProductCategory[];
  entries: BrandSelection[];
  onOpen: (slug: string) => void;
  emptyLabel: string;
}) {
  return (
    <SettingsCard>
      {categories.map((category) => (
        <PickerRow
          key={category.id}
          title={category.attributes.name}
          description={
            <BrandSelectionSummary
              entries={entries.filter(
                (entry) => entry.attributes.category === category.attributes.slug
              )}
              emptyLabel={emptyLabel}
              showSwatches
            />
          }
          multiline
          accessory={<Chevron />}
          onClick={() => onOpen(category.attributes.slug)}
        />
      ))}
    </SettingsCard>
  );
}

/* -------------------------------------------------------------------------- */
/*  One library — manufacturer → line → product, on its own axes              */
/* -------------------------------------------------------------------------- */

function CategoryBranch({
  category,
  brandId,
  lineId,
  axisChoice,
  preferredSlugs,
  search,
  isSearching,
  isFull,
  isCompact,
  entries,
  isPicked,
  onOpenBrand,
  onOpenLine,
  onAxisChange,
  onClearSearch,
  onToggle,
  t,
}: {
  category: ProductCategory;
  brandId: string | null;
  lineId: string | null;
  axisChoice: Record<string, string>;
  preferredSlugs: string[];
  search: string;
  isSearching: boolean;
  isFull: boolean;
  isCompact: boolean;
  entries: BrandSelection[];
  isPicked: (token: string) => boolean;
  onOpenBrand: (id: string) => void;
  onOpenLine: (id: string) => void;
  onAxisChange: (choice: Record<string, string>) => void;
  onClearSearch: () => void;
  onToggle: (token: string) => void;
  t: Translate;
}) {
  const slug = category.attributes.slug;
  const hasLineLevel = category.attributes.has_line_level;
  const axes = useTokenBearingAxes(category);

  // The explicit country filter. Null is not "no country" — the API still
  // arranges the shelf for where the user's house is and says which country
  // that was; null only means the user has not narrowed it themselves.
  const [country, setCountry] = useState<string | null>(null);

  const { data: brandsResult } = useCatalogBrands(slug, country);
  const brands = brandsResult?.brands ?? [];
  const markets = brandsResult?.markets ?? [];
  const arrangedFor = brandsResult?.countryCode ?? null;
  const availabilityNotice = brandsResult?.notices?.availability ?? null;
  const brand = brands.find((entry) => entry.id === brandId);
  const brandName = brand?.attributes.name;
  const brandWebsite = brand?.attributes.website_url ?? null;
  const countryName = useCountryName();

  const { data: lines = [] } = useCatalogLines(hasLineLevel ? slug : null, brandId);
  const line = lines.find((entry) => entry.id === lineId) ?? null;
  const lineWebsite = line?.attributes.website_url ?? null;

  // What the chip rows offer: the values this line is manufactured in, or the
  // library's own list where there is no line level to narrow it. EMPTY IS
  // REAL — a dozen siding lines carry the geometry inside their own name and
  // publish no separate list, so the picker must offer their products with no
  // axis step rather than show an empty shelf.
  const axisOptions = useMemo(() => {
    const available = line?.attributes.axis_values;

    return axes.map((axis) => ({
      axis,
      values:
        available !== undefined && available.length > 0
          ? available.filter((value) =>
              axis.attributes.values.some(
                (option) => option.attributes.slug === value.attributes.slug
              )
            )
          : hasLineLevel && line !== null
            ? []
            : axis.attributes.values,
    }));
  }, [axes, line, hasLineLevel]);

  // One value per axis is always chosen where the axis has any: landing on "no
  // value" would quietly drop the geometry from the render prompt. Arriving
  // from an existing pick, the values that pick was made on win over the first
  // in the list — otherwise the product it points at shows no badge.
  useEffect(() => {
    const next: Record<string, string> = {};

    for (const { axis, values } of axisOptions) {
      if (values.length === 0) continue;

      const current = axisChoice[axis.attributes.slug];
      if (current && values.some((v) => v.attributes.slug === current)) {
        next[axis.attributes.slug] = current;
        continue;
      }

      const preferred = values.find((value) =>
        preferredSlugs.includes(value.attributes.slug)
      );

      next[axis.attributes.slug] = (preferred ?? values[0]!).attributes.slug;
    }

    // Compared as a whole rather than value by value: an entry left over for an
    // axis the current line offers no values on has to GO, or it keeps riding
    // inside the product token as a geometry its maker never published it in.
    const keys = Object.keys(next);
    const isSettled =
      keys.length === Object.keys(axisChoice).length &&
      keys.every((key) => axisChoice[key] === next[key]);

    if (!isSettled) onAxisChange(next);
  }, [axisOptions, axisChoice, preferredSlugs, onAxisChange]);

  const chosenSlugs = useMemo(
    () => Object.values(axisChoice).filter(Boolean),
    [axisChoice]
  );

  // A single axis filters the list server-side through the published
  // availability matrix; with several, the first is the one worth narrowing by
  // (the rest only ride in the token).
  const filterSlug = chosenSlugs[0] ?? null;

  const {
    products,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCatalogProducts({
    category: slug,
    search,
    brandId: isSearching ? null : hasLineLevel ? null : brandId,
    lineId: isSearching ? null : lineId,
    axisValue: isSearching ? null : filterSlug,
    enabled: isSearching || (hasLineLevel ? lineId !== null : brandId !== null),
  });

  /**
   * A search hit is a way to FIND a product, not always to pick one: where a
   * library is faceted, the geometry is still unchosen, so opening a result
   * drops into its line rather than adding a token with no axis value.
   */
  const openSearchResult = (product: Product) => {
    onClearSearch();
    if (product.attributes.brand_id) onOpenBrand(product.attributes.brand_id);
    if (hasLineLevel) onOpenLine(product.attributes.line_id);
  };

  const isNavigationalSearch = axes.length > 0;

  if (isSearching) {
    if (isLoading) return <Spinner />;
    if (products.length === 0) return <Empty label={t('noResults')} />;

    return (
      <SettingsCard title={t('searchResults')}>
        {products.map((product) => {
          const token = buildBrandToken(slug, 'product', product.id);

          return (
            <PickerRow
              key={product.id}
              title={product.attributes.name}
              description={product.attributes.subtitle}
              multiline
              leading={<Swatch product={product} />}
              accessory={isNavigationalSearch ? <Chevron /> : undefined}
              isSelected={isNavigationalSearch ? undefined : isPicked(token)}
              isDisabled={
                isNavigationalSearch ? undefined : !isPicked(token) && isFull
              }
              onClick={() =>
                isNavigationalSearch ? openSearchResult(product) : onToggle(token)
              }
            />
          );
        })}
      </SettingsCard>
    );
  }

  if (!brandId) {
    return (
      <>
        {markets.length > 1 && (
          <CountryFilterCard
            markets={markets}
            selected={country}
            countryName={countryName}
            title={t('country')}
            allLabel={t('allCountries')}
            onSelect={setCountry}
          />
        )}
        <SettingsCard title={t('manufacturer')}>
          {brands.map((brand) => {
            const token = buildBrandToken(slug, 'brand', brand.id);
            // A brand-level pick is the row's own badge; anything picked DEEPER
            // is what this summary is for.
            const beneath = entries.filter(
              (entry) =>
                entry.attributes.category === slug &&
                entry.attributes.brand_id === brand.id &&
                entry.attributes.level !== 'brand'
            );

            // Only while the list is unfiltered. Once the user has picked a
            // country every row matches it, and a tag on all of them says
            // nothing. The tag NAMES the country the API arranged for rather
            // than one derived from the browser locale, which is the reader's
            // language and not where the wall stands.
            const isSoldHere =
              country === null &&
              arrangedFor !== null &&
              brand.attributes.is_sold_in_your_country === true;

            return (
              <PickerRow
                key={brand.id}
                title={brand.attributes.name}
                trailing={
                  isSoldHere ? (
                    <AvailabilityTag
                      label={t('availableIn', {
                        country: countryName(arrangedFor),
                      })}
                    />
                  ) : undefined
                }
                description={
                  beneath.length > 0 ? (
                    <BrandSelectionSummary entries={beneath} showSwatches />
                  ) : undefined
                }
                multiline
                isSelected={isPicked(token)}
                accessory={<Chevron />}
                onClick={() => onOpenBrand(brand.id)}
              />
            );
          })}
        </SettingsCard>
        {/* Said out loud, because an absent country is our ignorance and not a
            fact about the manufacturer. */}
        {availabilityNotice !== null && (
          <p className="text-card-meta text-muted-foreground px-4">
            {availabilityNotice}
          </p>
        )}
      </>
    );
  }

  if (hasLineLevel && !lineId) {
    return (
      <>
        <WholeBranchCard
          title={t('wholeBrand', { brand: brandName ?? '' })}
          token={buildBrandToken(slug, 'brand', brandId)}
          isPicked={isPicked}
          isFull={isFull}
          onToggle={onToggle}
        />
        {brandWebsite && (
          <ManufacturerWebsiteRow
            url={brandWebsite}
            label={t('manufacturerWebsite')}
          />
        )}
        <SettingsCard title={t('productLine')}>
          {lines.map((entry) => {
            // A line pick carries its chosen axis values inside its token, so
            // it is found by line id rather than by rebuilding one exact token.
            const linePick = entries.find(
              (selection) =>
                selection.attributes.level === 'line' &&
                selection.attributes.line_id === entry.id
            );

            const beneath = entries.filter(
              (selection) =>
                selection.attributes.line_id === entry.id &&
                selection.attributes.level === 'product'
            );

            return (
              <PickerRow
                key={entry.id}
                title={entry.attributes.name}
                description={
                  beneath.length > 0 ? (
                    <BrandSelectionSummary entries={beneath} showSwatches />
                  ) : (
                    (entry.attributes.material_label ?? undefined)
                  )
                }
                multiline
                isSelected={linePick !== undefined}
                accessory={<Chevron />}
                onClick={() => onOpenLine(entry.id)}
              />
            );
          })}
        </SettingsCard>
      </>
    );
  }

  return (
    <>
      {axisOptions.map(({ axis, values }) =>
        values.length === 0 ? null : (
          <div key={axis.id} className="space-y-1">
            <h3 className="text-section-eyebrow text-muted-foreground px-4 pt-1">
              {axis.attributes.name}
            </h3>
            <div className="flex flex-wrap gap-2">
              {values.map((value) => (
                <FilterChip
                  key={value.id}
                  label={value.attributes.name}
                  isSelected={
                    axisChoice[axis.attributes.slug] === value.attributes.slug
                  }
                  onClick={() =>
                    onAxisChange({
                      ...axisChoice,
                      [axis.attributes.slug]: value.attributes.slug,
                    })
                  }
                />
              ))}
            </div>
          </div>
        )
      )}

      <WholeBranchCard
        title={
          hasLineLevel
            ? t('anyInLine')
            : t('wholeBrand', { brand: brandName ?? '' })
        }
        // A library that tells the image model nothing on its own says so here
        // rather than implying the render will change.
        footer={category.attributes.informs_render ? undefined : t('brandNote')}
        token={
          hasLineLevel
            ? buildBrandToken(slug, 'line', lineId!, chosenSlugs)
            : buildBrandToken(slug, 'brand', brandId)
        }
        isPicked={isPicked}
        isFull={isFull}
        onToggle={onToggle}
      />

      {/* On EVERY screen inside a brand, not only the one where its shelf
          opens: "it is one step back, on the list of lines" is a rule the user
          cannot see, and somebody standing on a line's colours is exactly who
          wants to go and price it. */}
      {lineWebsite && (
        <ManufacturerWebsiteRow url={lineWebsite} label={t('lineWebsite')} />
      )}
      {brandWebsite && (
        <ManufacturerWebsiteRow
          url={brandWebsite}
          label={t('manufacturerWebsite')}
        />
      )}

      {isLoading ? (
        <Spinner />
      ) : products.length === 0 ? (
        <Empty label={t('noResults')} />
      ) : (
        <SettingsCard title={category.attributes.name}>
          {products.map((product) => {
            const token = buildBrandToken(slug, 'product', product.id, chosenSlugs);
            const isSelected = isPicked(token);

            return (
              <PickerRow
                key={product.id}
                title={product.attributes.name}
                description={rowSubtitle(product)}
                multiline
                leading={<Swatch product={product} />}
                isSelected={isSelected}
                isDisabled={!isSelected && isFull}
                onClick={() => onToggle(token)}
              />
            );
          })}
        </SettingsCard>
      )}

      {hasNextPage && (
        <LoadMore
          isLoading={isFetchingNextPage}
          isCompact={isCompact}
          label={t('loadMore')}
          onClick={() => void fetchNextPage()}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  The Selected strip                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A picked product, with removal on its own control.
 *
 * Not a `PickerRow`: that row is one button, and removal needs a second one
 * next to it (a button inside a button is invalid, and a whole-row remove is
 * what made a mis-tap cost the user a pick they can no longer trace). So the
 * row is a div that reveals the product in the tree, and the trash button is
 * the only thing that deletes.
 */
function SelectedPickRow({
  entry,
  removeLabel,
  onReveal,
  onRemove,
}: {
  entry: BrandSelection;
  removeLabel: string;
  onReveal: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onReveal}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onReveal();
        }
      }}
      className="w-full flex items-start gap-3 py-3.5 px-4 text-start border-b border-muted-foreground/25 last:border-b-0 transition-colors cursor-pointer hover:bg-foreground/5 active:bg-foreground/10"
    >
      <div className="flex-shrink-0 mt-0.5">
        <HexSwatch hex={entry.attributes.hex} />
      </div>

      <div className="flex-1 min-w-0">
        <span className="text-card-body text-foreground break-words">
          {entry.attributes.title}
        </span>
        {entry.attributes.subtitle && (
          <p className="text-list-item-subtitle text-muted-foreground mt-0.5 break-words">
            {entry.attributes.subtitle}
          </p>
        )}
      </div>

      <button
        type="button"
        aria-label={removeLabel}
        title={removeLabel}
        onClick={(event) => {
          // The row reveals, this button deletes — never both.
          event.stopPropagation();
          onRemove();
        }}
        className="flex-shrink-0 -mt-1 -me-1 p-2 rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Shared pieces                                                             */
/* -------------------------------------------------------------------------- */

/**
 * "Take everything under here" — the row that lets a pick stop above the
 * bottom of the tree. Pinned above the list it applies to, so drilling deeper
 * and settling for this level are two visibly different gestures.
 */
/**
 * The maker's own site, on the screen where its shelf is open.
 *
 * The catalogue's promise is that a picked product can actually be bought, and
 * a name alone does not say where. Rendered only where the manufacturer has a
 * verified URL — a missing one shows nothing rather than a guessed domain.
 */
/**
 * A country code turned into a country name, in the reader's language.
 *
 * The browser's own data rather than 250 strings per locale in our catalogues:
 * it is complete, it is correct in all 24 languages, and it is not ours to
 * maintain. Falls back to the bare code where a browser has no display names —
 * "DE" is worse than "Deutschland" and far better than nothing.
 */
function useCountryName() {
  const locale = useLocale();

  return useMemo(() => {
    let names: Intl.DisplayNames | null = null;

    try {
      names = new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      names = null;
    }

    return (code: string): string => {
      try {
        return names?.of(code) ?? code;
      } catch {
        return code;
      }
    };
  }, [locale]);
}

/** "Sold in Germany", next to a brand we KNOW is sold where the house is. */
function AvailabilityTag({ label }: { label: string }) {
  return (
    <span className="text-card-meta text-primary whitespace-nowrap">
      {label}
    </span>
  );
}

/**
 * Narrow the shelf to one country.
 *
 * The list is built from the catalogue itself, so the filter never offers a
 * country and then answers with an empty shelf. Sorted by the LOCALIZED name
 * rather than by how many brands serve each market: somebody opening this is
 * looking for their own country, not for the biggest one.
 */
function CountryFilterCard({
  markets,
  selected,
  countryName,
  title,
  allLabel,
  onSelect,
}: {
  markets: CatalogMarket[];
  selected: string | null;
  countryName: (code: string) => string;
  title: string;
  allLabel: string;
  onSelect: (country: string | null) => void;
}) {
  const options = useMemo(
    () =>
      markets
        .map((market) => ({ ...market, name: countryName(market.code) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [markets, countryName]
  );

  return (
    <SettingsCard>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'w-full flex items-center gap-3 py-3.5 px-4 text-start',
              'transition-colors cursor-pointer hover:bg-foreground/5 active:bg-foreground/10'
            )}
          >
            <span className="flex-1 min-w-0 text-card-body text-foreground truncate">
              {title}
            </span>
            <span className="text-card-body text-muted-foreground truncate">
              {selected === null ? allLabel : countryName(selected)}
            </span>
            <ChevronRight className="size-4 flex-shrink-0 text-muted-foreground rtl:-scale-x-100" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
          <DropdownMenuItem onSelect={() => onSelect(null)}>
            {allLabel}
          </DropdownMenuItem>
          {options.map((option) => (
            <DropdownMenuItem
              key={option.code}
              onSelect={() => onSelect(option.code)}
            >
              <span className="flex-1">{option.name}</span>
              <span className="text-muted-foreground">
                {option.brands_count}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SettingsCard>
  );
}

function ManufacturerWebsiteRow({ url, label }: { url: string; label: string }) {
  return (
    <SettingsCard>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'w-full flex items-center gap-3 py-3.5 px-4 text-start',
          'transition-colors cursor-pointer hover:bg-foreground/5 active:bg-foreground/10'
        )}
      >
        <span className="flex-1 min-w-0 text-card-body text-foreground truncate">
          {label}
        </span>
        <ExternalLink className="size-4 flex-shrink-0 text-muted-foreground" />
      </a>
    </SettingsCard>
  );
}

function WholeBranchCard({
  title,
  footer,
  token,
  isPicked,
  isFull,
  onToggle,
}: {
  title: string;
  footer?: string;
  token: string;
  isPicked: (token: string) => boolean;
  isFull: boolean;
  onToggle: (token: string) => void;
}) {
  const isSelected = isPicked(token);

  return (
    <SettingsCard footer={footer}>
      <PickerRow
        title={title}
        multiline
        isSelected={isSelected}
        isDisabled={!isSelected && isFull}
        onClick={() => onToggle(token)}
      />
    </SettingsCard>
  );
}

function Notices({ notices }: { notices: CatalogNotices }) {
  return (
    <div className="space-y-2 px-4 pb-1">
      <p className="text-card-meta text-muted-foreground">
        {notices.color_accuracy}
      </p>
      <p className="text-card-meta text-muted-foreground">{notices.attribution}</p>
    </div>
  );
}

/**
 * The path from the picker's root to the screen you are on.
 *
 * The tree is four levels deep and every level looks like the last one — a card
 * of rows with chevrons — so without this there is no way to tell a list of
 * lines from a list of manufacturers. Each segment above the current one jumps
 * straight to that level, which is also what makes ONE back button enough: Back
 * walks up a step, the trail skips as far up as you like.
 *
 * Names come from the catalogue (proper nouns), so only the root is localized.
 * Scrolls horizontally rather than truncating: a manufacturer and a line name
 * together outrun the panel's width, and the tail is the part that says where
 * you are.
 */
function Breadcrumb({
  steps,
}: {
  steps: Array<{ label: string; open: () => void }>;
}) {
  return (
    <nav className="flex items-center gap-1 overflow-x-auto text-card-meta">
      {steps.map((step, index) => {
        const isCurrent = index === steps.length - 1;

        return (
          <span key={`${step.label}-${index}`} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground rtl:-scale-x-100" />
            )}
            {isCurrent ? (
              <span className="whitespace-nowrap text-foreground">{step.label}</span>
            ) : (
              <button
                type="button"
                onClick={step.open}
                className="whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors"
              >
                {step.label}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function SearchField({
  value,
  onChange,
  placeholder,
  clearLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clearLabel: string;
}) {
  return (
    <div className="relative">
      <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="ps-9 pe-9"
        autoComplete="off"
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={clearLabel}
          className="absolute end-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:bg-foreground/10"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Row subtitle: the code where the maker publishes one, plus the honest flags.
 *
 * Both halves arrive composed and localized from the server — the caveats are
 * the difference between showing a product honestly and overselling it, and
 * re-deriving them here is how the rule drifted between platforms before.
 */
function rowSubtitle(product: Product): string | undefined {
  const parts = [
    ...(product.attributes.code ? [product.attributes.code] : []),
    ...product.attributes.caveats,
  ];

  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * 32×32 at radius 4 with a hairline border. A missing value draws an empty
 * dashed square, never a grey fill: a made-up swatch claims a colour the
 * manufacturer itself refuses to claim. A blend is hatched rather than flat,
 * because the stored value is only its median.
 */
function Swatch({ product }: { product: Product }) {
  const { hex, appearance_kind: kind, swatch_image_url: image } =
    product.attributes;

  if (image) {
    return (
      <div
        className="h-8 w-8 rounded border border-muted-foreground/30 bg-cover bg-center"
        style={{ backgroundImage: `url(${image})` }}
      />
    );
  }

  if (!hex) return <EmptySwatch />;

  return (
    <div
      className="h-8 w-8 rounded border border-muted-foreground/30"
      style={{
        backgroundColor: hex,
        backgroundImage:
          kind === 'solid'
            ? undefined
            : 'repeating-linear-gradient(45deg, rgba(0,0,0,0.16) 0 3px, rgba(255,255,255,0.16) 3px 6px)',
      }}
    />
  );
}

function HexSwatch({ hex }: { hex: string | null }) {
  if (!hex) return <EmptySwatch />;

  return (
    <div
      className="h-8 w-8 rounded border border-muted-foreground/30"
      style={{ backgroundColor: hex }}
    />
  );
}

function EmptySwatch() {
  return (
    <div
      className="h-8 w-8 rounded border border-dashed border-muted-foreground/60 bg-transparent"
      style={{
        backgroundImage:
          'linear-gradient(45deg, transparent 45%, currentColor 45%, currentColor 55%, transparent 55%)',
        color: 'color-mix(in oklch, currentColor 35%, transparent)',
      }}
    />
  );
}

function Chevron() {
  return (
    <ChevronRight className="h-4 w-4 text-muted-foreground rtl:-scale-x-100" />
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="text-center py-8 text-muted-foreground">{label}</div>;
}

function LoadMore({
  isLoading,
  isCompact,
  label,
  onClick,
}: {
  isLoading: boolean;
  isCompact: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      size={isCompact ? 'sm' : 'default'}
      disabled={isLoading}
      onClick={onClick}
    >
      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
    </Button>
  );
}

function FilterChip({
  label,
  isSelected,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-full text-chip whitespace-nowrap transition-colors',
        isSelected
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-muted-foreground hover:bg-muted/80'
      )}
    >
      {label}
    </button>
  );
}
